import { Add, AddCircle, Flaky } from '@mui/icons-material';
import { Alert, Box, Button } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import {
  ProposalPreviewProps,
  ProposalProps,
  ProposalStatus,
} from '~/data/proposals';
import { useVisible } from '~/hooks/useVisible';
import { EmployeeProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal';
import { EmployeeProposalUpdateCallback } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import {
  ProposalFormProps,
  ProposalFormPropsMessage,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/Common';
import { EmployeeProposalsHeader } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeProposalsLayout/components/EmployeeProposalsHeader';
import { EmployeeProposalsStatusCount } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeProposalsLayout/components/EmployeeProposalsStatusCount';
import { isEmployee } from '~/services/Permissions';

export type EmployeeProposalsLayoutProps<T extends ProposalProps> = {
  proposals: T[];
  proposalPreview?: ProposalPreviewProps;
  newProposal?: T;
  onProposalUpdate: EmployeeProposalUpdateCallback;
  user: {
    isInitiator: boolean;
  };
  formProps?: ProposalFormProps;
};
export const EmployeeProposalsLayout = <T extends ProposalProps>(
  props: EmployeeProposalsLayoutProps<T>,
) => {
  const { proposals, newProposal, onProposalUpdate, user, formProps } = props;

  const { t } = useTranslation();

  const roles = useLoggedInUserRoles();

  const hasProposals = useMemo(() => proposals.length > 0, [proposals]);
  const canInitiateNewProposal = useMemo(
    () => user.isInitiator && !!newProposal,
    [user, newProposal],
  );

  const proposalsListVisibility = useVisible(!hasProposals);
  const { isVisible, show } = proposalsListVisibility;

  const proposalsInProgress = useMemo((): ProposalProps[] => {
    return proposals.filter((p) =>
      [ProposalStatus.IN_PROGRESS, ProposalStatus.PROPOSED].includes(
        p.proposalStatus,
      ),
    );
  }, [proposals]);
  const isDefaultExpanded = useCallback(
    (proposal: ProposalProps): boolean => {
      return proposalsInProgress.length > 0
        ? proposalsInProgress[0].proposalId === proposal.proposalId
        : false;
    },
    [proposalsInProgress],
  );

  const getFormMessages = useCallback(
    (formProps?: ProposalFormProps): ProposalFormPropsMessage[] => {
      return !!formProps && 'messages' in formProps ? formProps.messages : [];
    },
    [],
  );

  const formMessages = useMemo(() => {
    const messages = getFormMessages(formProps);

    return {
      newProposal: messages.filter((msg) => msg.context === 'newProposal'),
    };
  }, [formProps, getFormMessages]);

  const renderMessages = useCallback((messages: ProposalFormPropsMessage[]) => {
    return messages.map((msg, index) => {
      return (
        <Alert severity={'info'} key={`formMessage-${index}`}>
          {msg.message}
        </Alert>
      );
    });
  }, []);

  return (
    <Box>
      {!canInitiateNewProposal && isEmployee(roles) ? (
        <>
          <Alert severity={'info'}>
            "You cannot see the pending proposals"
          </Alert>
        </>
      ) : hasProposals || !canInitiateNewProposal ? (
        <>
          <EmployeeProposalsHeader
            icon={<Flaky color={'info'} />}
            title={t('proposals')}
          >
            <EmployeeProposalsStatusCount
              proposals={proposals}
              status={ProposalStatus.IN_PROGRESS}
            />
            <EmployeeProposalsStatusCount
              proposals={proposals}
              status={ProposalStatus.COMPLETED}
            />
            <EmployeeProposalsStatusCount
              proposals={proposals}
              status={ProposalStatus.FAILED}
            />
          </EmployeeProposalsHeader>
          {proposals.map((proposal, index) => (
            <EmployeeProposal
              key={`proposal-${index}`}
              proposal={proposal}
              employeeId={proposal.targetId}
              defaultExpanded={isDefaultExpanded(proposal)}
              onProposalUpdate={onProposalUpdate}
              formProps={formProps}
            />
          ))}
          {proposals.length === 0 && (
            <Alert severity={'info'}>{t('noProposalsFound')}</Alert>
          )}
        </>
      ) : null}
      {canInitiateNewProposal && !!newProposal && (
        <>
          <EmployeeProposalsHeader
            icon={<AddCircle color={'info'} />}
            title={t('createNewProposal')}
          />
          {isVisible ? (
            <EmployeeProposal
              key={`proposal-${1000}`}
              proposal={newProposal}
              newProposalType={newProposal.proposalType}
              employeeId={newProposal.targetId}
              onProposalUpdate={(props) =>
                onProposalUpdate({ isNewCreated: true, ...props })
              }
              formProps={formProps}
            />
          ) : (
            <Button
              onClick={show}
              startIcon={<Add />}
              fullWidth={true}
              variant={'contained'}
              color={'primary'}
            >
              {t('addNew')}
            </Button>
          )}
        </>
      )}
      {renderMessages(formMessages.newProposal)}
    </Box>
  );
};
